import { Lock, Eye, EyeOff, CheckCircle } from "lucide-react";
import React, { useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { useNavigate } from "react-router-dom";

const UpdatePassword = () => {
  const { updateUserPassword } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const validatePassword = (pwd: string) => {
    const minLength = pwd.length >= 8;
    const hasUpperCase = /[A-Z]/.test(pwd);
    const hasLowerCase = /[a-z]/.test(pwd);
    const hasNumbers = /\d/.test(pwd);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);

    return {
      minLength,
      hasUpperCase,
      hasLowerCase,
      hasNumbers,
      hasSpecialChar,
      isValid:
        minLength &&
        hasUpperCase &&
        hasLowerCase &&
        hasNumbers &&
        hasSpecialChar,
    };
  };

  const passwordValidation = validatePassword(password);
  const passwordsMatch = password === confirmPassword && confirmPassword !== "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!passwordValidation.isValid) {
      setError("Please ensure your password meets all requirements.");
      return;
    }

    if (!passwordsMatch) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    setMessage("");
    setError("");

    try {
      const { error } = await updateUserPassword(password);
      if (error) {
        setError(error.message);
      } else {
        setMessage("Password updated successfully! Redirecting...");
        setTimeout(() => {
          navigate("/"); // Redirect to home or dashboard
        }, 2000);
      }
    } catch (err) {
      setError("Failed to update password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-linear-to-br from-primary-50 via-white to-neutral-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl border border-neutral-100 overflow-hidden">
          {/* Logo Section */}
          <div className="bg-white p-8 text-center border-b border-neutral-100">
            <img
              src="/assets/Images/logo/blackstore.png"
              alt="BlackStore"
              className="h-16 w-auto mx-auto mb-4"
            />
            <h2 className="text-2xl font-bold text-neutral-800 mb-2">
              Update Your Password
            </h2>
            <p className="text-neutral-600 text-sm">
              Enter your new password below.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6 p-8">
            {/* New Password */}
            <div>
              <label
                htmlFor="new-password"
                className="block text-sm font-medium text-neutral-700 mb-2"
              >
                New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                <input
                  id="new-password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 bg-neutral-50 focus:bg-white"
                  placeholder="Enter new password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>

              {/* Password Requirements */}
              {password && (
                <div className="mt-3 space-y-2">
                  <div className="text-xs text-neutral-600 mb-2">
                    Password must contain:
                  </div>
                  <div className="space-y-1">
                    <div
                      className={`flex items-center gap-2 text-xs ${
                        passwordValidation.minLength
                          ? "text-green-600"
                          : "text-neutral-500"
                      }`}
                    >
                      <CheckCircle
                        className={`w-3 h-3 ${
                          passwordValidation.minLength
                            ? "text-green-600"
                            : "text-neutral-300"
                        }`}
                      />
                      At least 8 characters
                    </div>
                    <div
                      className={`flex items-center gap-2 text-xs ${
                        passwordValidation.hasUpperCase
                          ? "text-green-600"
                          : "text-neutral-500"
                      }`}
                    >
                      <CheckCircle
                        className={`w-3 h-3 ${
                          passwordValidation.hasUpperCase
                            ? "text-green-600"
                            : "text-neutral-300"
                        }`}
                      />
                      One uppercase letter
                    </div>
                    <div
                      className={`flex items-center gap-2 text-xs ${
                        passwordValidation.hasLowerCase
                          ? "text-green-600"
                          : "text-neutral-500"
                      }`}
                    >
                      <CheckCircle
                        className={`w-3 h-3 ${
                          passwordValidation.hasLowerCase
                            ? "text-green-600"
                            : "text-neutral-300"
                        }`}
                      />
                      One lowercase letter
                    </div>
                    <div
                      className={`flex items-center gap-2 text-xs ${
                        passwordValidation.hasNumbers
                          ? "text-green-600"
                          : "text-neutral-500"
                      }`}
                    >
                      <CheckCircle
                        className={`w-3 h-3 ${
                          passwordValidation.hasNumbers
                            ? "text-green-600"
                            : "text-neutral-300"
                        }`}
                      />
                      One number
                    </div>
                    <div
                      className={`flex items-center gap-2 text-xs ${
                        passwordValidation.hasSpecialChar
                          ? "text-green-600"
                          : "text-neutral-500"
                      }`}
                    >
                      <CheckCircle
                        className={`w-3 h-3 ${
                          passwordValidation.hasSpecialChar
                            ? "text-green-600"
                            : "text-neutral-300"
                        }`}
                      />
                      One special character
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label
                htmlFor="confirm-password"
                className="block text-sm font-medium text-neutral-700 mb-2"
              >
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                <input
                  id="confirm-password"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 bg-neutral-50 focus:bg-white"
                  placeholder="Confirm new password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  disabled={loading}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>

              {/* Password Match Indicator */}
              {confirmPassword && (
                <div
                  className={`mt-2 text-xs ${
                    passwordsMatch ? "text-green-600" : "text-primary-600"
                  }`}
                >
                  {passwordsMatch
                    ? "✓ Passwords match"
                    : "✗ Passwords do not match"}
                </div>
              )}
            </div>

            {/* Messages */}
            {message && (
              <div className="text-green-600 text-sm bg-green-50 p-3 rounded-lg border border-green-200">
                {message}
              </div>
            )}
            {error && (
              <div className="text-primary-600 text-sm bg-primary-50 p-3 rounded-lg border border-primary-200">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={
                loading || !passwordValidation.isValid || !passwordsMatch
              }
              className="w-full bg-linear-to-r from-primary-600 to-primary-700 text-white py-3 rounded-lg font-semibold hover:from-primary-700 hover:to-primary-800 focus:ring-4 focus:ring-primary-200 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Updating..." : "Update Password"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdatePassword;
